import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  Breadcrumb as BaseBreadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import { useIsMobile } from '@/hooks/use-mobile';
import { Translate } from 'ra-core';

export const Breadcrumb = ({ children, ref }: BreadcrumbProps) => {
  const [mounted, setMounted] = React.useState(false);
  const portalRef = React.useRef<HTMLElement | null>(null);
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);

  // `useLayoutEffect` (not `useEffect`) so the portal target is resolved and
  // `mounted` flips to true *before the browser paints*. With a plain
  // `useEffect` the new breadcrumb rendered `null` for one painted frame on
  // every navigation — the flash. Dropped the manual `innerHTML = ''` reset:
  // React's portal already removes the previous page's breadcrumb nodes when
  // it unmounts (PageTransition uses `mode="wait"`, so there's never an
  // overlap), and the manual wipe was both redundant and a brief blank.
  React.useLayoutEffect(() => {
    portalRef.current = document.getElementById('breadcrumb');
    if (portalRef.current) {
      setMounted(true);
    }
  }, []);

  if (!mounted || !portalRef.current) return null;

  return createPortal(
    <>
      <BaseBreadcrumb ref={ref}>
        <BreadcrumbList>
          {isMobile && React.Children.count(children) > 2 ? (
            <React.Fragment>
              <BreadcrumbItem>
                <Drawer open={open} onOpenChange={setOpen}>
                  <DrawerTrigger aria-label="Toggle Menu">
                    <BreadcrumbEllipsis className="h-4 w-4" />
                  </DrawerTrigger>
                  <DrawerContent>
                    <DrawerHeader className="text-left">
                      <DrawerTitle>
                        <Translate i18nKey="ra.navigation.breadcrumb_drawer_title">
                          Navigate to
                        </Translate>
                      </DrawerTitle>
                      <DrawerDescription>
                        <Translate i18nKey="ra.navigation.breadcrumb_drawer_instructions">
                          Select a page to navigate to.
                        </Translate>
                      </DrawerDescription>
                    </DrawerHeader>
                    <ol className="grid gap-1 px-4">
                      {React.Children.toArray(children)
                        .slice(0, -1)
                        .map(item => item)}
                    </ol>
                    <DrawerFooter className="pt-4">
                      <DrawerClose asChild>
                        <Button variant="outline">
                          <Translate i18nKey="ra.action.close">Close</Translate>
                        </Button>
                      </DrawerClose>
                    </DrawerFooter>
                  </DrawerContent>
                </Drawer>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              {React.Children.toArray(children).slice(-1)}
            </React.Fragment>
          ) : (
            React.Children.map(
              children,
              (child, index) =>
                child && (
                  <React.Fragment key={index}>
                    {child}
                    {index < React.Children.count(children) - 1 ? (
                      <BreadcrumbSeparator />
                    ) : null}
                  </React.Fragment>
                )
            )
          )}
        </BreadcrumbList>
      </BaseBreadcrumb>
    </>,
    portalRef.current
  );
};
Breadcrumb.Item = BreadcrumbItem;
Breadcrumb.Link = BreadcrumbLink;
Breadcrumb.PageItem = BreadcrumbPage;

function BreadcrumbBadge({ children }: { children: React.ReactNode }) {
  // Matches the IO-number badge in CampaignHeader so the same value reads
  // identically wherever it appears (breadcrumb, header, etc.).
  return (
    <Badge
      variant="outline"
      className="px-2 py-0.5 font-medium bg-secondary rounded-sm whitespace-nowrap shrink-0 font-tabular"
    >
      {children}
    </Badge>
  );
}
Breadcrumb.Badge = BreadcrumbBadge;

export { BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbBadge };

export type BreadcrumbProps = React.ComponentProps<'nav'>;
